// src/lead-dados.ts
//
// Apresentação dos dados ricos do lead (GET /v1/leads/:id/dados). As CHAVES vêm
// do back em PT-BR (`buildLeadDados`, o mesmo dicionário que alimenta a IA na
// ligação); aqui mora só o que é de tela: rótulo, ordem e como o valor cru do
// HubSpot vira texto legível.
import i18n from "./i18n.js";

// Ordem de exibição — de utilidade numa ligação, não alfabética: capital
// primeiro (é o que qualifica), depois quem é a pessoa, depois o resto.
export const LEAD_DADO_LABELS: Record<string, string> = {
  marca_de_origem: "Marca de origem",
  capital_investimento: "Capital de investimento",
  capital_disponivel: "Capital disponível",
  disponibilidade_investimento: "Disponibilidade",
  // Sobe junto do capital porque qualifica com ele: quanto tem e para quando.
  tempo_para_iniciar: "Tempo para iniciar",
  cidade: "Cidade",
  // `cargo` antes de `ocupacao`/`profissao` — as três dizem a mesma coisa, mas é
  // `cargo` que o CRM preenche na maioria dos negócios; as outras duas são o
  // resto que sobra de formulários antigos.
  cargo: "Cargo",
  ocupacao: "Ocupação",
  profissao: "Profissão",
  idade: "Idade",
  sexo: "Sexo",
  genero: "Gênero",
  tem_socio: "Tem sócio",
  qtd_socios: "Qtd. de sócios",
  email: "E-mail",
  etapa: "Etapa no HubSpot",
  // Último de propósito: é texto livre do lead, o valor mais longo da lista, e
  // quebraria a leitura em coluna se ficasse no meio dos campos curtos.
  motivo_franquia: "Por que quer franquia",
  observacao_negocio: "Observação do negócio",
};

const ORDEM = Object.keys(LEAD_DADO_LABELS);

// O HubSpot guarda enum como slug: `de_r$_189_mil_até_r$_299_mil`. Vira
// "De R$ 189 mil até R$ 299 mil". Valor que já é texto normal passa intacto —
// só o trim, para não herdar espaço do CRM.
export function formatDadoValue(raw: string): string {
  const texto = raw.trim();
  if (!texto.includes("_")) return texto;
  const comEspacos = texto.replace(/_/g, " ").replace(/\s+/g, " ").trim();
  const comMoeda = comEspacos.replace(/\br\$/gi, "R$");
  return comMoeda.charAt(0).toUpperCase() + comMoeda.slice(1);
}

export interface LeadDadoRow {
  key: string;
  label: string;
  value: string;
}

// Linhas prontas para render: só as chaves PREENCHIDAS, na ordem de utilidade.
// Dado ausente não vira linha com "—" — o card some quando não há nada, em vez
// de ocupar espaço dizendo que não sabe nada do lead.
//
// Chave que o back mandar e não estiver no mapa vai para o FIM, com a chave
// virando rótulo. Um campo novo no back aparece torto, mas aparece; escondê-lo
// deixaria o vendedor sem uma informação que já existe.
export function leadDadosRows(dados: Record<string, string> | undefined): LeadDadoRow[] {
  if (!dados) return [];
  // Respostas antigas em cache podem conter a observação do contato.
  // Ela não deve aparecer como se fosse a observação do negócio.
  return Object.entries(dados)
    .filter(([key, v]) => key !== "observacao" && (v ?? "").trim() !== "")
    .sort(([a], [b]) => {
      const ia = ORDEM.indexOf(a);
      const ib = ORDEM.indexOf(b);
      if (ia === ib) return a.localeCompare(b);
      if (ia < 0) return 1;
      if (ib < 0) return -1;
      return ia - ib;
    })
    .map(([key, value]) => ({
      key,
      label: LEAD_DADO_LABELS[key]
        ? i18n.t(`leadData.labels.${key}`, { defaultValue: LEAD_DADO_LABELS[key] })
        : key.replace(/_/g, " "),
      value: key === "observacao_negocio" ? value.trim() : formatDadoValue(value),
    }));
}

// Linha secundária do item da fila: telefone e etapa do HubSpot, separados por
// ponto. Um dos dois pode faltar (o vendedor não vê telefone; o lead pode não
// ter etapa), então o separador só entra quando existem os dois.
export function queueItemSubtitle(item: {
  phone?: string | null;
  stage?: string | null;
}): string | null {
  const partes = [item.phone?.trim(), item.stage?.trim()].filter(
    (p): p is string => !!p && p !== "",
  );
  return partes.length ? partes.join(" · ") : null;
}
